import { ProcessCompleteResponse, ProcessProgressEvent } from '../types/processProgress';

interface StreamProcessOptions {
  url: string;
  body: Record<string, unknown>;
  onProgress: (event: ProcessProgressEvent) => void;
  signal?: AbortSignal;
}

function parseSseBlock(block: string): { event: string; data: string } | null {
  const trimmed = block.trim();
  if (!trimmed || trimmed.startsWith(':')) {
    return null;
  }

  let event = 'message';
  const dataLines: string[] = [];

  for (const line of trimmed.split('\n')) {
    if (line.startsWith('event:')) {
      event = line.slice(6).trim();
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trim());
    }
  }

  if (dataLines.length === 0) {
    return null;
  }

  return { event, data: dataLines.join('\n') };
}

export async function streamProcessRequest({
  url,
  body,
  onProgress,
  signal,
}: StreamProcessOptions): Promise<ProcessCompleteResponse> {
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    },
    body: JSON.stringify({ ...body, stream: true }),
    signal,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || `Request failed with status ${response.status}`);
  }

  if (!response.body) {
    throw new Error('Streaming response body is unavailable');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let finalResponse: ProcessCompleteResponse | null = null;

  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }

    buffer += decoder.decode(value, { stream: true });
    const blocks = buffer.split('\n\n');
    buffer = blocks.pop() || '';

    for (const block of blocks) {
      const parsed = parseSseBlock(block);
      if (!parsed) {
        continue;
      }

      const payload = JSON.parse(parsed.data);

      if (parsed.event === 'progress') {
        onProgress(payload as ProcessProgressEvent);
        continue;
      }

      if (parsed.event === 'complete') {
        finalResponse = payload as ProcessCompleteResponse;
        continue;
      }

      if (parsed.event === 'error') {
        throw new Error(payload.error || payload.message || 'Processing failed');
      }
    }
  }

  if (!finalResponse) {
    throw new Error('Stream ended without a completion event');
  }

  return finalResponse;
}
